// Automations section (R-C12, decision 008, um/2026-10-30/03 п.9): categories in the left column (AutomationsNav), the cards of the
// chosen category in the body. An open scenario stands beside the list of its category (the core's second column, ListReader): the
// list does not disappear, the open one is lit; on a phone the scenario takes the screen with «К списку». Back on the cards, the
// grid comes to the card that was open (quality eval 30.10).

import { useEffect, useMemo, useState } from 'react';
import { uuidv7, type AutomationDef, type AutomationSpec, type Id, type Step } from '@confdeck/core-shared';
import { useMeta, useSync } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { ListBody, ListEmpty, ListHead, ListReader, ListRow, StatusDot } from '../ui/ListReader.tsx';
import { TopBar, TopTitleInput } from '../ui/TopBar.tsx';
import { Page } from '../ui/Page.tsx';
import { ALL, catHref, useAutomationsTitle, useCategory } from './AutomationsNav.tsx';
import { inCategory } from './categories.ts';
import { createAutomation } from './create.ts';
import { IconButton, Button, Chip, Menu, MenuContent, MenuItem, MenuTrigger, Switch, Tabs, cx, useToast } from '../ui/primitives.tsx';
import { navigate } from '../shell/router.ts';
import { useUnsaved } from '../shell/unsaved.ts';
import { describeStep, describeTrigger } from './describe.ts';
import { Inspector, type Selection } from './Inspector.tsx';
import { useMailTemplates } from './mail-templates.ts';
import { RunLog } from './RunLog.tsx';
import s from './automations.module.css';

export function AutomationsScreen({ id }: { id: string | null }) {
  const meta = useMeta();
  const client = useSync();
  const selected = id ? meta.automation(id) : undefined;
  const isOwner = meta.user(client.header?.userId)?.role === 'owner';
  const cat = useCategory();
  if (!id) return <Cards />;
  return (
    <ListReader listLabel="Сценарии" readerLabel="Сценарий" reading onBack={() => navigate(catHref(cat))}
      list={<ScenarioList openId={id} />}
      reader={selected ? <Editor key={selected.id} a={selected} canEdit={isOwner} /> : <ListEmpty icon="zap" text="Сценарий не найден" />} />
  );
}

/**
 * The scenario opened last: the cards come back to it. It outlives the list beside the scenario (the cards are not mounted then),
 * so it lives beside the module, written in an effect, not in a render (MiniMax review 31.10, P3-9).
 */
let lastOpen: string | null = null;

/** A scenario keeps the category of the list in its address, so the list beside it does not change (`cat=all` — all of them). */
function useOpenHref() {
  const cat = useCategory();
  return (aid: string) => `/a/${aid}?cat=${encodeURIComponent(cat ?? ALL)}`;
}

function useNewScenario() {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const isOwner = meta.user(client.header?.userId)?.role === 'owner';
  return isOwner ? () => void createAutomation(client, meta, (text) => toast({ kind: 'error', text })) : null;
}

/** The scenarios of the category beside the open one: the core's list column, the open one lit and kept in view. */
function ScenarioList({ openId }: { openId: string }) {
  const meta = useMeta();
  const client = useSync();
  const cat = useCategory();
  const href = useOpenHref();
  const add = useNewScenario();
  const list = inCategory(meta.automations(), cat, client.menu);
  useEffect(() => { lastOpen = openId; document.getElementById(`auto-${openId}`)?.scrollIntoView({ block: 'nearest' }); }, [openId]);
  return (
    <>
      <ListHead title={useAutomationsTitle()} note={`${list.length}`} add={add ? { label: 'Сценарий', onClick: add } : undefined} />
      <ListBody label="Сценарии">
        {list.map((a) => (
          <ListRow key={a.id} id={`auto-${a.id}`} selected={a.id === openId} dot={a.enabled ? 'on' : 'off'} dotLabel={a.enabled ? 'Включён' : 'Выключен'}
            title={a.name} sub={describeTrigger(a.trigger, meta)} onClick={() => navigate(href(a.id))} />
        ))}
      </ListBody>
    </>
  );
}

/** The scenarios of the chosen category as cards: name, what starts it, on or off, standard or own. */
function Cards() {
  const meta = useMeta();
  const client = useSync();
  const cat = useCategory();
  const href = useOpenHref();
  const add = useNewScenario();
  const list = inCategory(meta.automations(), cat, client.menu);
  // Back from a scenario: the grid shows the card that was open, not its top.
  useEffect(() => { if (lastOpen) document.getElementById(`auto-${lastOpen}`)?.scrollIntoView({ block: 'center' }); }, []);
  return (
    <Page width="collection" title={useAutomationsTitle()} icon="zap" note={`${list.length}`}
      actions={add ? [{ key: 'new', label: 'Сценарий', icon: 'plus', primary: true, onClick: add }] : []}>
      {list.length ? (
        <div className={s.cards}>
          {list.map((a) => (
            <a key={a.id} id={`auto-${a.id}`} className={cx(s.card, a.id === lastOpen && s.cardOn)} href={href(a.id)} onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(href(a.id)); }}>
              <span className={s.cardHead}><StatusDot on={a.enabled} /><span className={s.cardName}>{a.name}</span></span>
              <span className={s.cardSub}>{describeTrigger(a.trigger, meta)}</span>
              {a.description && <span className={s.cardDesc}>{a.description}</span>}
              <span className={s.cardFoot}>{a.enabled ? 'Включён' : 'Выключен'} · шагов: {a.steps.length}{a.system && <Chip>стандартный</Chip>}</span>
            </a>
          ))}
        </div>
      ) : <div className={s.hint}>Сценариев здесь пока нет.</div>}
    </Page>
  );
}

function Editor({ a, canEdit }: { a: AutomationDef; canEdit: boolean }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const [draft, setDraft] = useState<AutomationSpec>(() => pick(a));
  const [sel, setSel] = useState<Selection>({ kind: 'trigger' });
  const [tab, setTab] = useState<'flow' | 'runs'>('flow');
  // A teammate's save replaces the working copy unless there are unsaved changes here.
  const saved = useMemo(() => JSON.stringify(pick(a)), [a]);
  const dirty = JSON.stringify(draft) !== saved;
  useEffect(() => { if (!dirty) setDraft(pick(a)); }, [saved]);
  const typeId = 'typeId' in draft.trigger ? draft.trigger.typeId : undefined;

  // Saving waits for the server, so the question before leaving knows whether the scenario is saved; a refusal is said.
  const save = async () => {
    const r = await client.run('automation.save', { id: a.id, ...draft });
    if (!r.ok) toast({ kind: 'error', text: r.error.message });
    return r.ok;
  };
  // An unsaved scenario is not lost by another scenario, another section, «назад» or closing the tab (shell/unsaved.ts).
  useUnsaved(dirty, save, `сценарий «${draft.name || a.name}»`);
  // The toast says what the server did, not what was asked: a refusal is said instead (Grok review 30.10, finding 21).
  const toggle = async () => {
    if (dirty) { toast({ kind: 'error', text: 'Сначала сохраните изменения' }); return; }
    const on = !a.enabled;
    const r = await client.run('automation.toggle', { id: a.id, enabled: on });
    toast(r.ok ? { kind: 'info', text: on ? 'Сценарий включён — сработает на следующих изменениях' : 'Сценарий выключен' } : { kind: 'error', text: r.error.message });
  };

  const insertStep = (at: number, step: Step) => setDraft((d) => ({ ...d, steps: [...d.steps.slice(0, at), step, ...d.steps.slice(at)] }));

  return (
    <section className={s.editor}>
      <TopBar heading={a.name} start={<TopTitleInput value={draft.name} disabled={!canEdit} onChange={(name) => setDraft({ ...draft, name })} label="Название сценария" />}
        middle={<input className={s.desc} value={draft.description} disabled={!canEdit} placeholder="Зачем этот сценарий" onChange={(e) => setDraft({ ...draft, description: e.target.value })} aria-label="Описание" />}
        end={canEdit && <Switch checked={a.enabled} onChange={() => void toggle()} label="Сценарий включён">{a.enabled ? 'Включён' : 'Выключен'}</Switch>}
        actions={canEdit ? [
          ...(dirty ? [{ key: 'undo', label: 'Отменить', icon: 'undo', onClick: () => setDraft(pick(a)) }, { key: 'save', label: 'Сохранить', icon: 'tick', primary: true, onClick: () => void save() }] : []),
          // A standard scenario is switched off, not removed (the server refuses it too); one's own goes to the archive (R-C12).
          ...(!a.system ? [{ key: 'archive', label: 'Удалить сценарий', icon: 'trash', danger: true, onClick: () => {
            if (!confirm(`Удалить сценарий «${a.name}»? Он перестанет срабатывать; журнал запусков останется.`)) return;
            client.mutate('automation.archive', { id: a.id }, [{ kind: 'automation', id: a.id, op: 'patch', data: { archivedAt: new Date().toISOString(), enabled: false } }]);
            toast({ kind: 'info', text: 'Сценарий удалён' });
          } }] : []),
        ] : []} />
      <Tabs bar label="Сценарий" value={tab} onChange={setTab} items={[{ key: 'flow', label: 'Схема' }, { key: 'runs', label: 'Запуски' }]} />
      {tab === 'runs' ? <RunLog automationId={a.id} /> : (
        <div className={s.body}>
          <div className={s.flow}>
            <Node kind="trigger" icon="zap" title="Когда" text={describeTrigger(draft.trigger, meta)} on={sel.kind === 'trigger'} onClick={() => setSel({ kind: 'trigger' })} />
            <Edge />
            <Node kind="cond" icon="filter" title="Условия" text={draft.conditions ? 'Только записи, подходящие под условия' : 'Для всех записей'} on={sel.kind === 'conditions'} onClick={() => setSel({ kind: 'conditions' })} />
            <Steps steps={draft.steps} typeId={typeId} path={[]} sel={sel} setSel={setSel} canEdit={canEdit}
              onInsert={(at, st) => insertStep(at, st)}
              onChange={(steps) => setDraft({ ...draft, steps })} />
            <div className={s.end}>Конец</div>
          </div>
          <Inspector draft={draft} setDraft={setDraft} sel={sel} canEdit={canEdit} onRemoved={() => setSel({ kind: 'trigger' })} />
        </div>
      )}
    </section>
  );
}

const pick = (a: AutomationDef): AutomationSpec => ({ name: a.name, description: a.description, trigger: a.trigger, conditions: a.conditions, steps: a.steps });

function Node({ icon, title, text, on, onClick, kind }: { icon: string; title: string; text: string; on: boolean; onClick: () => void; kind: string }) {
  return (
    <button type="button" className={cx(s.node, s[`node_${kind}`], on && s.nodeOn)} onClick={onClick}>
      <span className={s.nodeIcon}><Icon name={icon} size={15} /></span>
      <span className={s.nodeBody}><span className={s.nodeTitle}>{title}</span><span className={s.nodeText}>{text}</span></span>
    </button>
  );
}

const Edge = () => <span className={s.edge} aria-hidden />;

const NEW_STEPS: { type: Step['type']; label: string; icon: string }[] = [
  { type: 'createRecord', label: 'Создать запись', icon: 'plus' },
  { type: 'setField', label: 'Изменить поле', icon: 'text' },
  { type: 'sendMail', label: 'Отправить письмо', icon: 'mail' },
  { type: 'webhook', label: 'Вебхук', icon: 'external' },
  { type: 'wait', label: 'Подождать', icon: 'date' },
  { type: 'branch', label: 'Если…', icon: 'filter' },
  { type: 'stop', label: 'Остановить', icon: 'close' },
];

export function newStep(type: Step['type'], meta: ReturnType<typeof useMeta>, typeId?: Id): Step {
  const id = uuidv7().slice(-12);
  switch (type) {
    case 'createRecord': return { id, type, typeId: meta.types()[0]!.id, data: {} };
    case 'setField': return { id, type, field: typeId ? meta.fields(typeId).find((f) => f.fieldType !== 'relation' && !f.options.managed)?.key ?? 'name' : 'name', value: '' };
    case 'sendMail': return { id, type, template: '', to: '' };
    case 'webhook': return { id, type, url: '' };
    case 'wait': return { id, type, minutes: 1440 };
    case 'branch': return { id, type, if: { and: [] }, then: [], else: [] };
    case 'stop': return { id, type, reason: 'Дальше не идём' };
    case 'command': return { id, type, command: 'record.update', input: {} };
  }
}

function AddStep({ onAdd, nested, typeId }: { onAdd: (s: Step) => void; nested: boolean; typeId?: Id }) {
  const meta = useMeta();
  // The system's menu (R-U5: one dropdown in the whole system): keyboard, roles and layers come with it (audit 28.10, P1-5).
  return (
    <div className={s.add}>
      <Menu>
        <MenuTrigger asChild><IconButton size="sm" icon="plus" label="Добавить шаг" className={s.addBtn} /></MenuTrigger>
        <MenuContent align="center">
          {NEW_STEPS.filter((x) => !(nested && (x.type === 'wait' || x.type === 'branch'))).map((x) => (
            <MenuItem key={x.type} icon={x.icon} onSelect={() => onAdd(newStep(x.type, meta, typeId))}>{x.label}</MenuItem>
          ))}
        </MenuContent>
      </Menu>
    </div>
  );
}

function Steps({ steps, typeId, path, sel, setSel, canEdit, onInsert, onChange }: {
  steps: Step[]; typeId?: Id; path: number[]; sel: Selection; setSel: (s: Selection) => void; canEdit: boolean;
  onInsert: (at: number, s: Step) => void; onChange: (steps: Step[]) => void;
}) {
  const meta = useMeta();
  const templates = useMailTemplates();
  const nested = path.length > 0;
  return (
    <>
      {canEdit && <><Edge /><AddStep nested={nested} typeId={typeId} onAdd={(st) => { onInsert(0, st); setSel({ kind: 'step', path: [...path, 0] }); }} /></>}
      {steps.map((st, i) => {
        const here = [...path, i];
        const on = sel.kind === 'step' && sel.path.join('.') === here.join('.');
        return (
          <div key={st.id} className={s.stepWrap}>
            <Edge />
            <Node kind={st.type === 'branch' ? 'branch' : 'step'} icon={st.type === 'wait' ? 'date' : st.type === 'branch' ? 'filter' : st.type === 'stop' ? 'close' : st.type === 'sendMail' ? 'mail' : st.type === 'webhook' ? 'external' : 'zap'}
              title={`Шаг ${here.map((x) => x + 1).join('.')}`} text={describeStep(st, meta, typeId, templates)} on={on} onClick={() => setSel({ kind: 'step', path: here })} />
            {st.type === 'branch' && (
              <div className={s.branches}>
                {(['then', 'else'] as const).map((side) => (
                  <div key={side} className={s.branch}>
                    <span className={s.branchLabel}>{side === 'then' ? 'Да' : 'Нет'}</span>
                    <Steps steps={st[side]} typeId={typeId} path={[...here, side === 'then' ? 0 : 1]} sel={sel} setSel={setSel} canEdit={canEdit}
                      onInsert={(at, ns) => onChange(steps.map((x, j) => (j === i && x.type === 'branch' ? { ...x, [side]: [...x[side].slice(0, at), ns, ...x[side].slice(at)] } : x)))}
                      onChange={(inner) => onChange(steps.map((x, j) => (j === i && x.type === 'branch' ? { ...x, [side]: inner } : x)))} />
                  </div>
                ))}
              </div>
            )}
            {canEdit && <><Edge /><AddStep nested={nested} typeId={typeId} onAdd={(ns) => { onInsert(i + 1, ns); setSel({ kind: 'step', path: [...path, i + 1] }); }} /></>}
          </div>
        );
      })}
    </>
  );
}
